import type { Gender } from './types.js'

/**
 * Склонение русских имён по правилам плюс словарь исключений.
 * Порт `shared/.../RussianName.kt` один в один, включая поведение на краях:
 * редкие имена на -о/-е/-у/-и/-ю не склоняются, пустая строка возвращается как есть.
 */

const VOWELS = 'аеёиоуыэюя'
const HUSHING = 'жшщчцгкх' // после них пишется -и, а не -ы

/** родительный, дательный, творительный */
type Forms = readonly [string, string, string]

const exceptions: Record<string, Forms> = {
  лев: ['Льва', 'Льву', 'Львом'],
  павел: ['Павла', 'Павлу', 'Павлом'],
  любовь: ['Любови', 'Любови', 'Любовью'],
  пётр: ['Петра', 'Петру', 'Петром'],
  петр: ['Петра', 'Петру', 'Петром'],
}

const indeclinableFeminine = new Set(['николь', 'рашель', 'мишель', 'адель', 'нинель', 'эстель'])

/** Паши */
export function genitive(name: string, gender: Gender): string {
  return decline(name, gender, 0)
}

/** Паше */
export function dative(name: string, gender: Gender): string {
  return decline(name, gender, 1)
}

/** Пашей */
export function instrumental(name: string, gender: Gender): string {
  return decline(name, gender, 2)
}

/** проснулся / проснулась */
export function past(verbStemNoGender: string, gender: Gender): string {
  return verbStemNoGender + (gender === 'BOY' ? 'ся' : 'ась')
}

function matchCase(value: string, sample: string): string {
  const first = sample.charAt(0)
  return first !== '' && first === first.toUpperCase() && first !== first.toLowerCase()
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : value
}

function decline(raw: string, gender: Gender, form: 0 | 1 | 2): string {
  const name = raw.trim()
  if (name.length === 0) return name

  const exception = exceptions[name.toLowerCase()]
  if (exception) return matchCase(exception[form], name)

  const lower = name.toLowerCase()
  const last = lower.charAt(lower.length - 1)
  const stem = name.slice(0, -1)
  const preLast = lower.length > 1 ? lower.charAt(lower.length - 2) : ' '

  let forms: Forms
  if (lower.endsWith('ия')) {
    // Мария, Юлия
    forms = [`${stem}и`, `${stem}и`, `${stem}ей`]
  } else if (lower.endsWith('ья')) {
    // Дарья, Софья, Илья
    forms = gender === 'BOY' ? [`${stem}и`, `${stem}е`, `${stem}ёй`] : [`${stem}и`, `${stem}е`, `${stem}ей`]
  } else if (last === 'а') {
    // Паша, Маша, Никита, Лёва
    const gen = HUSHING.includes(preLast) ? `${stem}и` : `${stem}ы`
    const ins = 'жшщчц'.includes(preLast) ? `${stem}ей` : `${stem}ой`
    forms = [gen, `${stem}е`, ins]
  } else if (last === 'я') {
    // Ваня, Соня
    forms = [`${stem}и`, `${stem}е`, `${stem}ей`]
  } else if (last === 'й') {
    // Андрей, Сергей
    forms = [`${stem}я`, `${stem}ю`, `${stem}ем`]
  } else if (last === 'ь') {
    // Игорь (м), Любовь (ж), Николь (не склоняется)
    if (indeclinableFeminine.has(lower)) forms = [name, name, name]
    else if (gender === 'BOY') forms = [`${stem}я`, `${stem}ю`, `${stem}ем`]
    else forms = [`${stem}и`, `${stem}и`, `${stem}ью`]
  } else if (!VOWELS.includes(last)) {
    // Максим, Артём, Марк
    const ins = 'жшщч'.includes(last) ? `${name}ем` : `${name}ом`
    forms = [`${name}а`, `${name}у`, ins]
  } else {
    // Лео, Мари, Вилли — не склоняются
    forms = [name, name, name]
  }
  return forms[form]
}
